import { usePersistedState } from "@/lib/persistedFilters";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Bell, CalendarClock, Loader2, RefreshCw, Search } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { AlertSettingsDialog } from "./AlertSettingsDialog";
import { JobEditDialog } from "./JobEditDialog";
import { VIEWER_TZ, viewerDiffers } from "./jobMeta";
import { RunHistoryDialog } from "./RunHistoryDialog";
import { JobRow } from "./JobRow";

function Table({ title, hint, rows, canEdit, onEdit, onHistory, reload, testid }) {
  if (!rows.length) return null;
  return (
    <div data-testid={testid}>
      <div className="flex items-baseline gap-2 border-b border-slate-100 bg-slate-50/60 px-4 py-2">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-600">{title}</h4>
        <span className="text-[11px] text-slate-500">{rows.length} {hint}</span>
      </div>
      <table className="w-full table-fixed text-sm">
        <colgroup><col className="w-[42%]" /><col className="w-[18%]" /><col className="w-[20%]" /><col className="w-[5%]" /><col className="w-[15%]" /></colgroup>
        <thead><tr className="text-left text-[10px] uppercase tracking-wide text-slate-400">
          <th className="px-4 pt-2 font-semibold">Job &amp; note</th><th className="px-3 pt-2 font-semibold">When</th>
          <th className="px-3 pt-2 font-semibold">Last run</th><th className="px-3 pt-2 font-semibold">On</th><th /></tr></thead>
        <tbody>{rows.map((j) => <JobRow key={j.key} j={j} canEdit={canEdit} onEdit={onEdit} onHistory={onHistory} reload={reload} />)}</tbody>
      </table>
    </div>
  );
}

/** Settings → Platform → Scheduled jobs. */
export function ScheduledJobsPanel() {
  const [d, setD] = useState(null);
  const [q, setQ] = usePersistedState("scheduled-jobs.q", "");
  const [group, setGroup] = usePersistedState("scheduled-jobs.group", "");
  const [edit, setEdit] = useState(null);
  const [hist, setHist] = useState(null);
  const [alerts, setAlerts] = useState(false);
  const load = useCallback(() => api.get("/cron/jobs").then(({ data }) => setD(data)).catch(() => setD(false)), []);
  useEffect(() => { load(); }, [load]);
  const groups = useMemo(() => (d ? [...new Set(d.jobs.map((j) => j.group))].sort() : []), [d]);
  if (d === null) return <Card className="p-6"><Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-300" /></Card>;
  if (!d) return <Card className="p-6 text-sm text-slate-500" data-testid="jobs-error">Could not load the scheduled jobs.</Card>;
  const match = (j) => (!q || `${j.label} ${j.key} ${j.notes}`.toLowerCase().includes(q.toLowerCase()));
  const jobs = d.jobs.filter((j) => match(j) && (!group || j.group === group));
  const off = d.jobs.filter((j) => !j.enabled).length;
  const props = { canEdit: d.can_edit, onEdit: setEdit, onHistory: setHist, reload: load };
  const failing = [...d.jobs, ...d.timers].filter((j) => j.fail_streak > 1).length;
  const labels = Object.fromEntries([...d.jobs, ...d.timers].map((j) => [j.key, j.label]));
  return (
    <Card className="overflow-hidden border-slate-200 shadow-sm" data-testid="scheduled-jobs-panel">
      <div className="flex flex-wrap items-start gap-3 border-b border-slate-100 px-5 py-4">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-50 text-amber-600"><CalendarClock className="h-4 w-4" /></div>
        <div className="min-w-0 flex-1">
          <h3 className="font-heading text-base font-semibold text-slate-800">Scheduled jobs</h3>
          <p className="text-xs text-slate-500">Everything the app runs on its own: {d.jobs.length} scheduled jobs, {d.timers.length} background timers and the platform trigger. Times are {d.timezone} (Mountain){viewerDiffers() && <span data-testid="jobs-viewer-tz">; your own time ({VIEWER_TZ}) is shown underneath</span>}. Jobs are checked every {d.tick_minutes} minutes.
            {off > 0 && <b className="text-amber-700" data-testid="jobs-off-count"> {off} switched off.</b>}
            {failing > 0 && <b className="text-rose-700" data-testid="jobs-failing-count"> {failing} failing repeatedly.</b>}
            {!d.can_edit && " You can view them; changing needs Platform settings edit access."}</p>
        </div>
        <Button size="sm" variant="outline" onClick={() => setAlerts(true)} data-testid="jobs-alerts-button"
                className={d.alert_recipients ? "" : "border-amber-300 text-amber-800"}>
          <Bell className="h-3.5 w-3.5" /> Failure alerts
          <span className="rounded bg-slate-100 px-1 text-[10px] font-semibold text-slate-600" data-testid="jobs-alerts-count">{d.alert_recipients || "not set"}</span></Button>
        <Button size="sm" variant="ghost" onClick={load} data-testid="jobs-refresh"><RefreshCw className="h-3.5 w-3.5" /> Refresh</Button>
      </div>
      <div className="flex flex-wrap gap-2 border-b border-slate-100 px-5 py-3">
        <div className="relative w-72"><Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, id or note…" className="h-9 pl-8 text-xs" data-testid="jobs-search" /></div>
        <select value={group} onChange={(e) => setGroup(e.target.value)} className="h-9 rounded-md border border-slate-200 bg-white px-2 text-xs" data-testid="jobs-group">
          <option value="">All areas</option>{groups.map((g) => <option key={g} value={g}>{g}</option>)}</select>
      </div>
      <div className="max-h-[75vh] overflow-auto">
        <Table title="Platform trigger" hint="calls the app" rows={match(d.platform) && !group ? [d.platform] : []} testid="jobs-platform" {...props} />
        {(group ? [group] : groups).map((g) => (
          <Table key={g} title={g} hint="jobs" rows={jobs.filter((j) => j.group === g)} testid={`jobs-group-${g.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`} {...props} />))}
        <Table title="Background timers" hint="run inside the app while it is awake" rows={group ? [] : d.timers.filter(match)} testid="jobs-timers" {...props} />
        {!jobs.length && !d.timers.filter(match).length && <p className="p-8 text-center text-sm text-slate-500" data-testid="jobs-empty">Nothing matches.</p>}
      </div>
      {edit && <JobEditDialog job={edit} tz={d.timezone} onClose={() => setEdit(null)} onSaved={load} />}
      {hist && <RunHistoryDialog job={hist} onClose={() => setHist(null)} />}
      {alerts && <AlertSettingsDialog canEdit={d.can_edit} labels={labels} onClose={() => { setAlerts(false); load(); }} />}
    </Card>
  );
}
